import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

import type { Json } from "@/integrations/supabase/types";
import type { OrgRole } from "@/lib/rbac/roles";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  billing_email: string | null;
  plan: string;
  settings: Json;
  created_at: string;
  updated_at: string;
  // Multi-tenant HQ fields
  subdomain: string | null;
  is_active: boolean;
  parent_org_id: string | null;
  org_type: 'personal' | 'agency' | 'client';
  // New: AccountType + Plan architecture
  account_type: 'START' | 'PARTNER_HQ' | 'ENTERPRISE';
  plan_id: string;
  sub_account_count: number;
}

export interface OrgSettings {
  branding?: {
    logo_url?: string;
    primary_color?: string;
    favicon_url?: string;
  };
  features?: {
    custom_domain_enabled?: boolean;
    white_label?: boolean;
  };
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgRole;
  joined_at: string;
  profiles?: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

export interface OrganizationInvite {
  id: string;
  organization_id: string;
  email: string;
  role: OrgRole;
  token: string;
  invited_by: string | null;
  status: string;
  expires_at: string;
  created_at: string;
}

export function useOrganizations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['organizations', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as Organization[];
    },
    enabled: !!user,
  });
}

export function useOrganization(orgId: string | null) {
  return useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (error) throw error;
      return data as Organization;
    },
    enabled: !!orgId,
  });
}

export function useOrganizationMembers(orgId: string | null) {
  return useQuery({
    queryKey: ['organization-members', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      
      // First get members
      const { data: members, error: membersError } = await supabase
        .from('organization_members')
        .select('*')
        .eq('organization_id', orgId)
        .order('joined_at');

      if (membersError) throw membersError;
      if (!members || members.length === 0) return [];

      // Then get profiles for those members using the safe view
      const userIds = members.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles_safe')
        .select('id, display_name, avatar_url')
        .in('id', userIds);

      // Merge profiles with members
      return members.map(member => ({
        ...member,
        profiles: profiles?.find(p => p.id === member.user_id) || null,
      })) as OrganizationMember[];
    },
    enabled: !!orgId,
  });
}

export function useOrganizationInvites(orgId: string | null) {
  return useQuery({
    queryKey: ['organization-invites', orgId],
    queryFn: async () => {
      if (!orgId) return [];
      
      const { data, error } = await supabase
        .from('organization_invites')
        .select('*')
        .eq('organization_id', orgId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as OrganizationInvite[];
    },
    enabled: !!orgId,
  });
}

export function useUserOrgRole(orgId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-org-role', orgId, user?.id],
    queryFn: async () => {
      if (!orgId || !user) return null;
      
      const { data, error } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', orgId)
        .eq('user_id', user.id)
        .single();

      if (error) return null;
      return data.role as 'owner' | 'admin' | 'member' | 'viewer';
    },
    enabled: !!orgId && !!user,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; slug: string; logoUrl?: string; billingEmail?: string }) => {
      const { data: result, error } = await supabase.functions.invoke('create-organization', {
        body: data,
      });

      if (error) throw error;
      if (result.error) throw new Error(result.error);
      
      return result.organization as Organization;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organisatie aangemaakt!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kon organisatie niet aanmaken');
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<Organization> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('organizations')
        .update({
          name: updateData.name,
          slug: updateData.slug,
          logo_url: updateData.logo_url,
          billing_email: updateData.billing_email,
          plan: updateData.plan,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result as Organization;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization', data.id] });
      toast.success('Organisatie bijgewerkt!');
    },
    onError: () => {
      toast.error('Kon organisatie niet bijwerken');
    },
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { organizationId: string; email: string; role: string }) => {
      const { data: result, error } = await supabase.functions.invoke('invite-member', {
        body: data,
      });

      if (error) throw error;
      if (result.error) throw new Error(result.error);
      
      return result.invite;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-invites', variables.organizationId] });
      toast.success('Uitnodiging verstuurd!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kon uitnodiging niet versturen');
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const { data: result, error } = await supabase.functions.invoke('accept-invite', {
        body: { token },
      });

      if (error) throw error;
      if (result.error) throw new Error(result.error);
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Je bent toegevoegd aan de organisatie!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kon uitnodiging niet accepteren');
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, orgId }: { memberId: string; orgId: string }) => {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      return { memberId, orgId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', variables.orgId] });
      toast.success('Lid verwijderd');
    },
    onError: () => {
      toast.error('Kon lid niet verwijderen');
    },
  });
}

export function useCancelInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, orgId }: { inviteId: string; orgId: string }) => {
      const { error } = await supabase
        .from('organization_invites')
        .update({ status: 'cancelled' })
        .eq('id', inviteId);

      if (error) throw error;
      return { inviteId, orgId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-invites', variables.orgId] });
      toast.success('Uitnodiging geannuleerd');
    },
    onError: () => {
      toast.error('Kon uitnodiging niet annuleren');
    },
  });
}
